/**
 * L'atelier graphique — l'AIMANTATION d'un contact (doc 26, § 2.1).
 *
 * Dans un rayon donné en unités de figure (22 pt à l'écran, converti au zoom
 * courant par l'appelant), dans cet ordre :
 *
 *  1. un point existant (énoncé ou élève) ;
 *  2. l'intersection de deux objets tracés ;
 *  3. un objet (le point y resterait attaché : `point_sur`, phase 2) ;
 *  4. un nœud de la grille, si « Aimant » est actif (`aimanterALaGrille`) ;
 *  5. sinon, la position du contact, bornée au repère (`borner`).
 *
 * **Jamais d'aimantation vers la solution** : on aimante vers ce qui est
 * VISIBLE — le calque quand il est affiché, les objets de l'élève qu'il n'a
 * pas masqués —, jamais vers ce qui est attendu. Cette fonction ne reçoit
 * d'ailleurs aucun attendu.
 *
 * Fonction pure, partagée : les voies de saisie produisent la même cible.
 */
import { estIdEnonce, } from './construction.js';
import { aimanterALaGrille, borner } from './coordonnees.js';
import { evaluerFonctionV2 } from './figure-v2.js';
import { distance, distancePointCercle, distancePointCourbe, intersectionCercles, intersectionDroiteCercle, intersectionDroites, plusProcheSurLigne, } from './geometrie.js';
/** Tolérance d'appartenance à la fenêtre et aux bornes d'un segment. */
const EPS = 1e-9;
/* ================================================= paramétrage sur un objet */
/**
 * Le point d'un objet tracé le plus proche de `p`, sa distance, et son
 * paramètre `t` — l'abscisse curviligne normalisée de `point_sur` :
 *
 *  - ligne : a + t·(b − a) (segment : t ∈ [0, 1] ; demi-droite : t ≥ 0) ;
 *  - cercle : l'angle depuis l'axe des x, en tours, t ∈ [0, 1) ;
 *  - polygone : la fraction du périmètre depuis le premier sommet ;
 *  - fonction : la fraction de [x0, x1], le domaine coupé à la fenêtre.
 *
 * `null` : l'objet n'a pas de tracé où poser un point.
 */
export function projeterSurObjet(o, p, repere) {
    switch (o.genre) {
        case 'ligne': {
            const r = plusProcheSurLigne(p, o.a, o.b, o.sorte);
            return { p: r.q, t: r.t, d: r.d };
        }
        /* Un vecteur se parcourt comme le segment qu'il dessine : t va de son
           origine à sa pointe. Le demi-plan, lui, n'offre que sa frontière —
           elle seule est une ligne où poser un point. */
        case 'vecteur': {
            const r = plusProcheSurLigne(p, o.de, o.a, 'segment');
            return { p: r.q, t: r.t, d: r.d };
        }
        case 'demi_plan': {
            const r = plusProcheSurLigne(p, o.a, o.b, 'droite');
            return { p: r.q, t: r.t, d: r.d };
        }
        case 'cercle': {
            const dx = p.x - o.centre.x;
            const dy = p.y - o.centre.y;
            const theta = Math.hypot(dx, dy) < EPS ? 0 : Math.atan2(dy, dx);
            const t = (((theta / (2 * Math.PI)) % 1) + 1) % 1;
            return {
                p: surCercleTours(o.centre, o.rayon, t),
                t,
                d: distancePointCercle(p, o.centre, o.rayon),
            };
        }
        case 'polygone': {
            const n = o.sommets.length;
            const longueurs = o.sommets.map((s, i) => distance(s, o.sommets[(i + 1) % n]));
            const perimetre = longueurs.reduce((s, l) => s + l, 0);
            if (!(perimetre > EPS))
                return null;
            let meilleur = null;
            let cumul = 0;
            o.sommets.forEach((s, i) => {
                const r = plusProcheSurLigne(p, s, o.sommets[(i + 1) % n], 'segment');
                const t = (cumul + r.t * longueurs[i]) / perimetre;
                if (meilleur === null || r.d < meilleur.d)
                    meilleur = { p: r.q, t: t % 1, d: r.d };
                cumul += longueurs[i];
            });
            return meilleur;
        }
        case 'fonction': {
            const [x0, x1] = domaineVisible(o.domaine, repere);
            if (!(x1 > x0))
                return null;
            const r = distancePointCourbe(p, (x) => evaluerFonctionV2(o.f, x), x0, x1);
            return r === null ? null : { p: r.q, t: (r.x - x0) / (x1 - x0), d: r.d };
        }
        default:
            return null;
    }
}
/** Le point d'un objet tracé au paramètre `t` (l'inverse de `projeterSurObjet`) ; `null` sans tracé. */
export function pointSurObjet(o, t, repere) {
    if (!Number.isFinite(t))
        return null;
    switch (o.genre) {
        case 'ligne': {
            const u = o.sorte === 'segment'
                ? Math.min(1, Math.max(0, t))
                : o.sorte === 'demi_droite'
                    ? Math.max(0, t)
                    : t;
            return { x: o.a.x + (o.b.x - o.a.x) * u, y: o.a.y + (o.b.y - o.a.y) * u };
        }
        case 'vecteur': {
            const u = Math.min(1, Math.max(0, t));
            return { x: o.de.x + (o.a.x - o.de.x) * u, y: o.de.y + (o.a.y - o.de.y) * u };
        }
        case 'demi_plan':
            return { x: o.a.x + (o.b.x - o.a.x) * t, y: o.a.y + (o.b.y - o.a.y) * t };
        case 'cercle':
            return surCercleTours(o.centre, o.rayon, t);
        case 'polygone': {
            const n = o.sommets.length;
            const longueurs = o.sommets.map((s, i) => distance(s, o.sommets[(i + 1) % n]));
            const perimetre = longueurs.reduce((s, l) => s + l, 0);
            if (!(perimetre > EPS))
                return null;
            let reste = (((t % 1) + 1) % 1) * perimetre;
            for (let i = 0; i < n; i += 1) {
                const l = longueurs[i];
                if (reste <= l || i === n - 1) {
                    const a = o.sommets[i];
                    const b = o.sommets[(i + 1) % n];
                    const u = l > EPS ? Math.min(1, reste / l) : 0;
                    return { x: a.x + (b.x - a.x) * u, y: a.y + (b.y - a.y) * u };
                }
                reste -= l;
            }
            return null;
        }
        case 'fonction': {
            const [x0, x1] = domaineVisible(o.domaine, repere);
            if (!(x1 > x0))
                return null;
            const x = x0 + (x1 - x0) * Math.min(1, Math.max(0, t));
            const y = evaluerFonctionV2(o.f, x);
            return Number.isFinite(y) ? { x, y } : null;
        }
        default:
            return null;
    }
}
function surCercleTours(c, r, t) {
    const a = 2 * Math.PI * t;
    return { x: c.x + r * Math.cos(a), y: c.y + r * Math.sin(a) };
}
function domaineVisible(domaine, r) {
    return [Math.max(domaine?.[0] ?? r.xMin, r.xMin), Math.min(domaine?.[1] ?? r.xMax, r.xMax)];
}
function morceauxDe(o) {
    switch (o.genre) {
        case 'ligne':
            return [{ id: o.id, ligne: { a: o.a, b: o.b, sorte: o.sorte } }];
        case 'vecteur':
            return [{ id: o.id, ligne: { a: o.de, b: o.a, sorte: 'segment' } }];
        case 'demi_plan':
            return [{ id: o.id, ligne: { a: o.a, b: o.b, sorte: 'droite' } }];
        case 'cercle':
            return [{ id: o.id, cercle: { c: o.centre, r: o.rayon } }];
        case 'polygone':
            return o.sommets.map((s, i) => ({
                id: o.id,
                ligne: {
                    a: s,
                    b: o.sommets[(i + 1) % o.sommets.length],
                    sorte: 'segment',
                },
            }));
        default:
            return [];
    }
}
const distanceMorceau = (m, p) => 'ligne' in m
    ? plusProcheSurLigne(p, m.ligne.a, m.ligne.b, m.ligne.sorte).d
    : distancePointCercle(p, m.cercle.c, m.cercle.r);
/** Le point `q`, trouvé sur la droite porteuse, est-il sur le morceau lui-même ? */
function surMorceau(m, q) {
    if (!('ligne' in m) || m.ligne.sorte === 'droite')
        return true;
    const { a, b } = m.ligne;
    const ab = { x: b.x - a.x, y: b.y - a.y };
    const d2 = ab.x * ab.x + ab.y * ab.y;
    if (d2 < EPS)
        return false;
    const t = ((q.x - a.x) * ab.x + (q.y - a.y) * ab.y) / d2;
    const tol = 1e-7;
    return m.ligne.sorte === 'segment' ? t >= -tol && t <= 1 + tol : t >= -tol;
}
/** Les solutions de deux morceaux, chacune avec sa branche. */
function intersections(m, n) {
    let sols = [];
    if ('ligne' in m && 'ligne' in n) {
        const q = intersectionDroites(m.ligne.a, m.ligne.b, n.ligne.a, n.ligne.b);
        sols = q === null ? [] : [q];
    }
    else if ('ligne' in m && 'cercle' in n) {
        sols = intersectionDroiteCercle(m.ligne.a, m.ligne.b, n.cercle.c, n.cercle.r) ?? [];
    }
    else if ('cercle' in m && 'ligne' in n) {
        sols = intersectionDroiteCercle(n.ligne.a, n.ligne.b, m.cercle.c, m.cercle.r) ?? [];
    }
    else if ('cercle' in m && 'cercle' in n) {
        sols = intersectionCercles(m.cercle.c, m.cercle.r, n.cercle.c, n.cercle.r) ?? [];
    }
    return sols
        .map((p, i) => ({ p, branche: (i === 0 ? 0 : 1) }))
        .filter((s) => surMorceau(m, s.p) && surMorceau(n, s.p));
}
/**
 * La cible de l'aimant pour un contact en `p` (unités de figure), dans un
 * rayon `rayon` (unités de figure). Voir l'en-tête pour l'ordre.
 */
export function aimanter(p, resolue, rayon, options = {}) {
    const r = resolue.repere;
    const exclus = new Set(options.exclure ?? []);
    const dedans = (q) => q.x >= r.xMin - EPS && q.x <= r.xMax + EPS && q.y >= r.yMin - EPS && q.y <= r.yMax + EPS;
    const visible = (o) => !exclus.has(o.id) && (estIdEnonce(o.id) ? options.calque !== false : o.masque !== true);
    const objets = [...resolue.objets.values()].filter(visible);
    if (rayon > 0) {
        /* 1. un point existant */
        let point = null;
        for (const o of objets) {
            if (o.genre !== 'point' || !dedans(o.p))
                continue;
            const d = distance(p, o.p);
            if (d <= rayon && (point === null || d < point.d))
                point = { id: o.id, p: o.p, d };
        }
        if (point !== null)
            return { genre: 'point', id: point.id, p: point.p };
        /* 2. l'intersection de deux objets tracés, parmi ceux qui passent dans le rayon */
        const proches = objets.flatMap(morceauxDe).filter((m) => distanceMorceau(m, p) <= rayon);
        let croisement = null;
        for (let i = 0; i < proches.length; i += 1) {
            for (let j = i + 1; j < proches.length; j += 1) {
                const m = proches[i];
                const n = proches[j];
                if (m.id === n.id)
                    continue;
                for (const s of intersections(m, n)) {
                    const d = distance(p, s.p);
                    if (d <= rayon && dedans(s.p) && (croisement === null || d < croisement.d)) {
                        croisement = { de: m.id, a: n.id, branche: s.branche, p: s.p, d };
                    }
                }
            }
        }
        if (croisement !== null) {
            const { de, a, branche, p: q } = croisement;
            return { genre: 'intersection', de, a, branche, p: q };
        }
        /* 3. un objet */
        let objet = null;
        for (const o of objets) {
            const s = projeterSurObjet(o, p, r);
            if (s === null || s.d > rayon || !dedans(s.p))
                continue;
            if (objet === null || s.d < objet.d)
                objet = { id: o.id, t: s.t, p: s.p, d: s.d };
        }
        if (objet !== null)
            return { genre: 'objet', id: objet.id, t: objet.t, p: objet.p };
    }
    /* 4. la grille ; 5. le contact, borné au repère */
    if (options.grille)
        return { genre: 'grille', p: aimanterALaGrille(p, options.grille, r) };
    return { genre: 'libre', p: borner(p, r) };
}
//# sourceMappingURL=aimanter.js.map